import { EnhancementDay } from '../../models/enhancement'

const toNumberWithMax2DecimalPlaces = (num: string): number => {
  let result = num.slice(0, num.indexOf('.') + 3)

  const roundToQuater = (afterDedimal: string): string => {
    if (afterDedimal.length === 2) return afterDedimal
    return (Math.round(Number(afterDedimal) * 4) / 4).toFixed(2).toString()
  }

  if (result.includes('.')) {
    const afterDedimal = result.substring(result.indexOf('.') - 1, result.length)
    const upToDecimal = result.substring(0, result.indexOf('.') - 1)

    result = upToDecimal.concat(roundToQuater(afterDedimal))
  }

  return Number(result)
}

/**
 * Validates and processes hours input for enhancement requests (Call Out, On Call, Night, Overtime, Weekend).
 *
 * This function handles the specific business logic for enhancement requests:
 * - Rounds hours to the nearest quarter (0.25) with max 2 decimal places
 * - Enforces bounds: 0-24 hours
 * - Auto-unchecks the day if hours is set to 0 or negative
 * - Preserves the current checked state if hours > 0
 *
 * @param hours - The hours input as a string (from user input)
 * @param currentChecked - The current checked state of the day
 * @returns Object containing validated hours (number) and updated checked state (boolean)
 *
 * @example
 * // User enters 8.3 hours for a checked day
 * validateEnhancementHours("8.3", true) // returns { hours: 8.25, checked: true }
 *
 * // User enters 0 hours for a checked day (auto-uncheck)
 * validateEnhancementHours("0", true) // returns { hours: 0, checked: false }
 *
 * // User enters negative hours
 * validateEnhancementHours("-2", true) // returns { hours: 0, checked: false }
 */
export const validateEnhancementHours = (
  hours: string,
  currentChecked: boolean
): { hours: number; checked: boolean } => {
  let val = toNumberWithMax2DecimalPlaces(hours)
  if (val > 24) val = 24
  if (val < 0) val = 0
  
  const checked = Number(hours) > 0 ? currentChecked : false
  
  return { hours: val, checked }
}

/**
 * Validates and processes hours input for standard absence requests (Holiday, Day Off, Lieu Day, etc.).
 *
 * This function handles the business logic for standard absence requests:
 * - Converts string input to float
 * - Enforces bounds: 0-24 hours
 * - Does not affect checked state (handled separately)
 *
 * @param hours - The hours input as a string (from user input)
 * @returns Object containing validated hours (number)
 *
 * @example
 * // User enters 8 hours
 * validateAbsenceHours("8") // returns { hours: 8.0 }
 *
 * // User enters negative hours
 * validateAbsenceHours("-2") // returns { hours: 0.0 }
 *
 * // User enters excessive hours
 * validateAbsenceHours("30") // returns { hours: 24.0 }
 */
export const validateAbsenceHours = (hours: string): { hours: number } => {
  let hoursToFloat = parseFloat(hours)
  if (hoursToFloat < 0) {
    hoursToFloat = 0.0
  }
  if (hoursToFloat > 24) {
    hoursToFloat = 24.0
  }
  
  return { hours: hoursToFloat }
}

